/**
 * Wire contracts for role assignment (PR #5a.4).
 *
 * A role template is a named bundle of permissions defined per entity
 * type (see `docs/USER-ENTITY-MODEL.md` §3). This PR does NOT edit the
 * templates themselves — it assigns existing templates to users, subject
 * to three rules enforced server-side:
 *
 *   1. entity-type match — a brokerage user can only hold brokerage roles.
 *   2. Segregation-of-Duties — some role pairs are mutually exclusive.
 *   3. last-admin guard — the final AdminSuperUser cannot be removed.
 *
 * The exclusion pairs are returned alongside the template list so the
 * "add role" picker can grey out conflicting options before the user
 * even submits.
 */

import { z } from 'zod';

export const RoleTemplateSchema = z.object({
  id: z.string().uuid(),
  entityTypeSlug: z.string().min(1),
  slug: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string(),
  /** ZAR string; NULL = no bind authority from this role. */
  defaultBindLimit: z.string().nullable(),
  /** Decimal fraction string (e.g. "0.20"); NULL = n/a. */
  defaultDiscretionaryRangePct: z.string().nullable(),
  isSystemRole: z.boolean(),
});

export const RoleExclusionSchema = z.object({
  roleATemplateId: z.string().uuid(),
  roleBTemplateId: z.string().uuid(),
  reason: z.string().min(1),
});

export const RoleTemplateListQuerySchema = z.object({
  entityTypeSlug: z.string().trim().min(1).optional(),
});

export const RoleTemplateListResponseSchema = z.object({
  roles: z.array(RoleTemplateSchema),
  exclusions: z.array(RoleExclusionSchema),
});

/** A single role-assignment record returned by GET /admin/users/:id/roles. */
export const UserRoleAssignmentSchema = z.object({
  roleTemplateId: z.string().uuid(),
  slug: z.string().min(1),
  displayName: z.string().min(1),
  entityTypeSlug: z.string().min(1),
  assignedAt: z.string().datetime(),
  assignedByUserId: z.string().uuid().nullable(),
});

export const UserRolesResponseSchema = z.object({
  roles: z.array(UserRoleAssignmentSchema),
});

export const RoleAssignRequestSchema = z.object({
  roleTemplateId: z.string().uuid(),
});

export type RoleTemplate = z.infer<typeof RoleTemplateSchema>;
export type RoleExclusion = z.infer<typeof RoleExclusionSchema>;
export type RoleTemplateListQuery = z.infer<typeof RoleTemplateListQuerySchema>;
export type RoleTemplateListResponse = z.infer<typeof RoleTemplateListResponseSchema>;
export type UserRoleAssignment = z.infer<typeof UserRoleAssignmentSchema>;
export type UserRolesResponse = z.infer<typeof UserRolesResponseSchema>;
export type RoleAssignRequest = z.infer<typeof RoleAssignRequestSchema>;
